import { withApiTimeout } from '@/helpers/ApiTimeout/ApiTimeout';
import {
  ALLOWED_PUBLIC_TRANSPORT,
  ALLOWED_TRAVEL_TIME_POIS,
  CustomPOITravelTimeData,
  PublicTransportSurroundingInfo,
  SurroundingInfoResponse,
  TravelTimeData,
  TravelTimePOIKey,
  TravelTimeTransportationType,
} from '@/store/types/travel-time-state';
import { getRetryingAxiosInstance } from '@smg-real-estate/hg-search-libs';

// KK-1447: Timeout constant for surrounding info API calls
export const SURROUNDING_INFO_TIMEOUT_MS = 500;
// KK-1992: Longer timeout for persona-based POI requests (more data to fetch)
const SURROUNDING_INFO_PERSONA_TIMEOUT_MS = 3000;

export const travelTimeAxiosInstance = getRetryingAxiosInstance({
  timeout: 4000,
  retryConfig: {
    retries: 2,
  },
});

export const TRAVEL_TIME_ENTRY_POINT_HEADER = 'x-travel-time-entry-point';

export const TravelTimeEntryPoint = {
  TRAVEL_TIME: 'pdp_travel-time',
  NEIGHBOURHOOD: 'pdp_neighborhood',
} as const;

export type TravelTimeEntryPoint = (typeof TravelTimeEntryPoint)[keyof typeof TravelTimeEntryPoint];

export function getTravelTimeEntryPointHeaders(
  entryPoint: TravelTimeEntryPoint,
): Record<string, string> {
  return {
    [TRAVEL_TIME_ENTRY_POINT_HEADER]: entryPoint,
  };
}

export function formatTravelTime(travelTime: number | undefined, hasUserPOI?: boolean): string {
  if (typeof travelTime != 'number' || isNaN(travelTime)) {
    return '-';
  }

  const minutes = Math.round(travelTime / 60);

  if (minutes < 1) {
    return '1 min.';
  } else if (minutes < 60) {
    return `${minutes} min.`;
  }

  // Different formatting for times greater than 1 hour
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (remainingMinutes === 0) {
    return minutes >= 180 ? '> 3h' : `${hours}h`;
  }
  if (hasUserPOI) {
    if (minutes >= 180) {
      return '> 3h';
    }
    return `${hours}h ${remainingMinutes.toString().padStart(2, '0')}`;
  } else {
    return '> 60 min';
  }
}

export async function buildSurroundingInfoEndpoint(
  listingId: string,
  latitude: number,
  longitude: number,
  persona?: string,
): Promise<URL> {
  const endpoint = new URL(
    `${import.meta.env.VUE_POINTS_OF_INTEREST_API_ENDPOINT}/surrounding-info/${listingId}`,
  );

  endpoint.searchParams.set('coords', [latitude, longitude].join(','));

  if (persona) {
    const { getPersonaPOIs, formatPOIsForAPI } =
      await import('@/helpers/Neighbourhood/personaPOIMapping');
    const poiKeys = getPersonaPOIs(persona);
    endpoint.searchParams.set('poi', formatPOIsForAPI(poiKeys));
  }

  return endpoint;
}

// Helper to fetch surrounding info with timeout
// Exported for use by neighbourhood store
export async function fetchSurroundingInfo(
  listingId: string,
  latitude: number,
  longitude: number,
  options: {
    entryPoint: TravelTimeEntryPoint;
    persona?: string;
  },
) {
  const { entryPoint, persona } = options;
  const endpoint = await buildSurroundingInfoEndpoint(listingId, latitude, longitude, persona);
  const timeoutMs = persona ? SURROUNDING_INFO_PERSONA_TIMEOUT_MS : SURROUNDING_INFO_TIMEOUT_MS;

  return withApiTimeout(
    (signal) =>
      travelTimeAxiosInstance.get(endpoint.toString(), {
        signal,
        headers: getTravelTimeEntryPointHeaders(entryPoint),
      }),
    {
      timeoutMs,
      timeoutErrorMessage: 'SURROUNDING_INFO_TIMEOUT',
    },
  );
}

export const mapCustomPOIResponse = (
  response: any,
): { listingId: string; data: TravelTimeData }[] => {
  return Object.entries(response.data).map(([listingId, rawTravelTimeData]) => {
    const { travelTime, name, type, transportation } = rawTravelTimeData as CustomPOITravelTimeData;
    const data = [
      {
        key: type,
        time: travelTime,
        name,
        transportation,
      },
    ];

    return {
      listingId,
      data,
    };
  });
};

/**
 * Helper to extract and sort public transport POIs
 */
function extractPublicTransport(
  rawTravelTimeData: SurroundingInfoResponse[],
): SurroundingInfoResponse[] {
  return rawTravelTimeData
    .filter(
      (type: SurroundingInfoResponse) =>
        type[0] && ALLOWED_PUBLIC_TRANSPORT.includes(type[0] as PublicTransportSurroundingInfo),
    )
    ?.sort((a, b) => a[1].time - b[1].time);
}

/**
 * Helper to add station POI from public transport data
 */
function addStationFromPublicTransport(
  travelTimings: TravelTimeData,
  publicTransport: SurroundingInfoResponse[],
): void {
  if (publicTransport?.[0]) {
    const index = travelTimings.findIndex((val) => val.key === TravelTimePOIKey.STATION);
    const station = {
      key: TravelTimePOIKey.STATION,
      time: publicTransport[0]?.[1]?.time,
      name: publicTransport[0]?.[1]?.name,
    };
    index > -1 ? (travelTimings[index] = station) : travelTimings.push(station);
  }
}

/**
 * Core processing function for surrounding info response
 * @param data - Raw API response data
 * @param filterToBasePOIs - If true, filters to only ALLOWED_TRAVEL_TIME_POIS (4 base types)
 */
export function processSurroundingInfoCore(data: any, filterToBasePOIs: boolean): TravelTimeData {
  const rawTravelTimeData: SurroundingInfoResponse[] = Object.entries(
    data[TravelTimeTransportationType.WALKING],
  ) as SurroundingInfoResponse[];

  const publicTransport = extractPublicTransport(rawTravelTimeData);

  const travelTimings = rawTravelTimeData.reduce((acc, [key, value]) => {
    const travelTimeKey = key as TravelTimePOIKey;

    // Skip public transport types as they're handled separately
    if (ALLOWED_PUBLIC_TRANSPORT.includes(key as PublicTransportSurroundingInfo)) return acc;

    // Apply filtering based on use case
    if (filterToBasePOIs && !ALLOWED_TRAVEL_TIME_POIS.includes(travelTimeKey)) return acc;

    const { time, name } = value;
    return acc.concat([{ key: travelTimeKey, time, name }]);
  }, [] as TravelTimeData);

  addStationFromPublicTransport(travelTimings, publicTransport);

  return travelTimings;
}
